import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ComfortIcon from '../components/icons/ComfortIcon';
import icon_building from '../assets/icon_building.svg';

import { useCampusMeta, useCampusData, useCampusWeather } from '../context/CampusContextProvider';
import { ComfortOverview } from '../features/Comfort/ComfortOverview';

import { Header } from '../components/Header/Header';
import { Breadcrumb } from '../components/Breadcrumb/Breadcrumb';
import ZoneCard from '../components/ZoneCard/ZoneCard';
import { MarketingMessageThermalZones } from '../components/MarketingMessage/MarketingMessageThermalZones';
import { SectionCollapseable } from '../components/SectionCollapseable/SectionCollapseable';

import './Comfort.scss';

const Comfort = ({ currentZoneId, showMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const campusMeta = useCampusMeta();
  const campusWeather = useCampusWeather();
  const {
    data: campusData,
    isLoading: campusDataLoading,
    error: campusDataError,
  } = useCampusData();

  const [sortedCampusBuildings, setSortedCampusBuildings] = useState([]);
  const [hasZones, setHasZones] = useState(null);

  const campusName = campusMeta ? campusMeta.campusName : '';
  const currentCampusData = campusData;

  useEffect(() => {
    if (currentCampusData && currentCampusData.buildings) {
      getBuildingsArray();
      // setHasZones(currentCampusData.buildings.some((building) => building.thermalZones.length > 0));
      setHasZones(
        currentCampusData.buildings.some(
          (building) => building.thermalZones && building.thermalZones.length > 0
        )
      );
    }
  }, [currentCampusData]);

  // When data updates, reload
  useEffect(() => {
    // console.log('currentCampusData', currentCampusData);

    if (hasZones === false) {
      return;
    }
  }, [currentCampusData, currentZoneId, hasZones]);

  const getBuildingsArray = () => {
    let tempBuildings = [];
    if (currentCampusData.buildings.length === 1) {
      tempBuildings = [...currentCampusData.buildings];
    } else if (currentCampusData.buildings) {
      tempBuildings = [...currentCampusData.buildings].sort((a, b) => {
        if (a.buildingDescription.buildingEntityKey - b.buildingDescription.buildingEntityKey) {
          return 1;
        }
        if (a.buildingDescription.buildingEntityKey < b.buildingDescription.buildingEntityKey) {
          return -1;
        }
        return 0;
      });
    }

    for (let i = 0; i < tempBuildings.length; i++) {
      if (!tempBuildings[i].thermalZones) return 0;
      tempBuildings[i].thermalZones.sort((a, b) => {
        if (a.longThermalZoneDisplayName - b.longThermalZoneDisplayName) {
          return 1;
        }
        if (a.longThermalZoneDisplayName < b.longThermalZoneDisplayName) {
          return -1;
        }
        return 0;
      });
    }

    setSortedCampusBuildings(tempBuildings);
  };

  const navigateWithValidation = (newPath) => {
    if (location.pathname !== newPath) {
      navigate(newPath);
    }
  };

  const selectZone = (selectedZoneId, buildingId) => {
    if (selectedZoneId && currentCampusData?.campusDescription?.campusEntityKey) {
      navigateWithValidation(
        '/comfort/' +
          currentCampusData.campusDescription.campusEntityKey +
          '/' +
          buildingId +
          '/' +
          selectedZoneId
      );
    }
  };

  const renderHeaderLockup = () => {
    return (
      <>
        <ComfortIcon
          backgroundFill="none"
          fillColor={'#02222B'}
          strokeColor={'#02222B'}
          strokeWidth={2.5}
          width="18px"
        />
        <h1>Comfort</h1>
      </>
    );
  };

  return (
    <section className={`comfort-section ${showMobile ? 'mobile' : 'desktop'}`}>
      <Header siteName={campusName} weatherData={campusWeather}>
        <Breadcrumb iconHeader={renderHeaderLockup()} />
      </Header>

      <div className="comfort-content">
        {!hasZones && !campusDataLoading ? (
          <MarketingMessageThermalZones />
        ) : (
          <>
            <ComfortOverview campusData={currentCampusData} />
            {sortedCampusBuildings.map((building, index) => {
              return (
                <React.Fragment key={`building-${index}`}>
                  <div className="building-container">
                    <SectionCollapseable
                      customHeading={
                        <div className="building-header">
                          <img src={icon_building} alt="building icon" width="20" height="20" />
                          <h3 className="building-name">
                            {building.buildingDescription.longBuildingDisplayName}
                          </h3>
                        </div>
                      }
                    >
                      {building.thermalZones.length !== 0 ? (
                        <div className="thermal-zones--list ">
                          <div className="labels">
                            <div className="head location header-text--sm">Location</div>
                            <div className="head temp-range header-text--sm">
                              Temp{' '}
                              <span style={{ fontSize: '11px', color: '#62797e' }}>(Settings)</span>
                            </div>
                            <div className="head activity header-text--sm">Activity</div>
                            <div className="head keypad header-text--sm">Keypad</div>
                            <div className="head control header-text--sm">Active Control</div>
                            <div className="head schedule header-text--sm">Schedule</div>
                          </div>
                          <div className="zones">
                            {building.thermalZones.map((zoneData, index) => (
                              <React.Fragment
                                key={`${zoneData.shortThermalZoneDisplayName}${index}`}
                              >
                                <ZoneCard
                                  zoneRealtimeData={zoneData}
                                  selectZone={() =>
                                    selectZone(
                                      zoneData.thermalZoneEntityKey,
                                      building.buildingDescription.buildingEntityKey
                                    )
                                  }
                                />
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="building-empty-state">
                          <ComfortIcon
                            backgroundFill="none"
                            fillColor={'#02222B'}
                            strokeColor={'#02222B'}
                            strokeWidth={2.5}
                            width="18px"
                          />
                          <p>There are no zones to display.</p>
                        </div>
                      )}
                    </SectionCollapseable>
                  </div>
                </React.Fragment>
              );
            })}
          </>
        )}
      </div>
    </section>
  );
};

export default Comfort;
